import { useMutation } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaMutationOptions } from '@frontend/react-query-helpers';
import { isSmsMessageItem, isCallRecordItem } from '../../utils/thread-items-utils';
import { useUnifiedThreadUpdater } from '../query-updaters';
import { SchemaSMSDataV4Service } from '../service';
import { DismissTagIO } from '../types';

export const getDismissTagMutationKey = () => ['V4', 'DismissTag'] as const;

const ZeroUUID = '00000000-0000-0000-0000-000000000000';

type MutationContext<C = unknown> = {
  mutationContext: { didOptimisticUpdate: boolean; revertOptimisticUpdate?: () => void };
  otherContext?: C;
};

type UseDismissTagMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends DismissTagIO['input'] = DismissTagIO['input']
> = {
  options?: LimitedSchemaMutationOptions<DismissTagIO, E, C, never, RequestOverride>;
  httpOptions?: Options;
  optimisticUpdate?: boolean;
};
/**
 * A hook that returns a mutation for the `DismissTag` mutation endpoint.
 * It handles unified thread cache updates with optimistic updates.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `true`.
 */
export const useDismissTagMutation = <
  E = unknown,
  C = unknown,
  RequestOverride extends DismissTagIO['input'] = DismissTagIO['input']
>({
  options,
  httpOptions,
  optimisticUpdate = true,
}: UseDismissTagMutationArgs<E, C | undefined, RequestOverride> = {}) => {
  const unifiedThreadUpdater = useUnifiedThreadUpdater({ httpOptions });

  return useMutation<DismissTagIO['output'], E, RequestOverride, MutationContext<C>>({
    mutationKey: getDismissTagMutationKey(),
    mutationFn: (req) => SchemaSMSDataV4Service.DismissTag(req, httpOptions),
    ...options,
    onMutate: async (request) => {
      const mutationContext: MutationContext<C>['mutationContext'] = {
        didOptimisticUpdate: false,
      };

      if (optimisticUpdate) {
        // Use unified thread updater for optimistic updates
        const upsertThreadItemResult = await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadItemId: request.id,
            threadId: request.threadId,
            locationId: request.locationId,
          },
          updater: (threadItem) => {
            if (!threadItem) return threadItem;

            if (isSmsMessageItem(threadItem) && threadItem.smsMessage) {
              return {
                ...threadItem,
                smsMessage: {
                  ...threadItem.smsMessage,
                  tags: threadItem.smsMessage.tags.filter((id) => id !== request.tagId),
                  tagsDetailed: threadItem.smsMessage.tagsDetailed?.filter((tag) => tag.tagId !== request.tagId),
                },
              };
            }
            if (isCallRecordItem(threadItem) && threadItem.callRecord) {
              return {
                ...threadItem,
                callRecord: {
                  ...threadItem.callRecord,
                  tags: threadItem.callRecord.tags?.filter((tag) => tag.tagId !== request.tagId) || [],
                },
              };
            }
            return threadItem;
          },
          isOptimistic: true,
          getThreadOnly: true,
        });

        mutationContext.didOptimisticUpdate = true;
        mutationContext.revertOptimisticUpdate = () => {
          upsertThreadItemResult.revertCacheUpdate();
        };
      }

      return {
        mutationContext,
        otherContext: await options?.onMutate?.(request),
      };
    },
    onSuccess: async (response, request, context) => {
      if (!context?.mutationContext.didOptimisticUpdate) {
        // Apply changes if optimistic update wasn't done
        await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadItemId: request.id,
            threadId: request.threadId,
            locationId: request.locationId,
          },
          updater: (threadItem) => {
            if (!threadItem) return threadItem;

            if (isSmsMessageItem(threadItem) && threadItem.smsMessage) {
              return {
                ...threadItem,
                smsMessage: {
                  ...threadItem.smsMessage,
                  tags: threadItem.smsMessage.tags.filter((id) => id !== request.tagId),
                  tagsDetailed: threadItem.smsMessage.tagsDetailed?.filter((tag) => tag.tagId !== request.tagId),
                },
              };
            }
            if (isCallRecordItem(threadItem) && threadItem.callRecord) {
              return {
                ...threadItem,
                callRecord: {
                  ...threadItem.callRecord,
                  tags: threadItem.callRecord.tags?.filter((tag) => tag.tagId !== request.tagId) || [],
                },
              };
            }
            return threadItem;
          },
          isOptimistic: false,
          getThreadOnly: true,
        });

        // Update unified thread uniqueTags - remove the dismissed tag
        await unifiedThreadUpdater.upsertUnifiedThread({
          matchValues: {
            threadId: request.threadId,
            locationId: request.locationId,
          },
          updater: (thread) => {
            if (!thread) return undefined;
            const latestTaggedEntity = response.latestTaggedEntity;
            if (latestTaggedEntity?.tagId) {
              return {
                ...thread,
                uniqueTags: thread.uniqueTags?.map((tag) =>
                  tag.tagId === latestTaggedEntity.tagId
                    ? {
                        tagId: latestTaggedEntity.tagId,
                        entityId: latestTaggedEntity.entityId,
                        entityCreatedAt: latestTaggedEntity.entityCreatedAt || '',
                        appliedBy: latestTaggedEntity.appliedBy || ZeroUUID,
                        entityType: latestTaggedEntity.entityType,
                      }
                    : tag
                ),
              };
            } else {
              return {
                ...thread,
                uniqueTags: thread.uniqueTags?.filter((tag) => tag.tagId !== request.tagId),
              };
            }
          },
          isOptimistic: false,
        });
      }

      return options?.onSuccess?.(response, request, context?.otherContext);
    },
    onError: (error, request, context) => {
      if (context?.mutationContext.didOptimisticUpdate) {
        context.mutationContext.revertOptimisticUpdate?.();
      }

      return options?.onError?.(error, request, context?.otherContext);
    },
    onSettled: (response, error, request, context) => {
      return options?.onSettled?.(response, error, request, context?.otherContext);
    },
  });
};
